import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  IconButton,
  TextField,
  Stack,
  useTheme,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import BusinessIcon from "@mui/icons-material/Business";
import MessageIcon from "@mui/icons-material/Message";
import LabelRoundedIcon from "@mui/icons-material/LabelRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";

const MotionCard = motion.create ? motion.create(Card) : motion(Card);
const MotionBox = motion.create ? motion.create(Box) : motion(Box);

const RESERVED_CUSTOM_FIELDS = ["lead_source", "order_cost", "city", "full_name", "full name"];
const HIDDEN_KEYS = ["meta_raw_field_data", "meta_leadgen_id", "meta_phone_raw", "meta_created_time"];

// Flatten meta_raw_field_data [{name, values}] into {name: value} pairs
const flattenMetaFields = (customFields) => {
  const flat = {};
  const rawData = customFields?.meta_raw_field_data;
  if (Array.isArray(rawData)) {
    rawData.forEach((field) => {
      if (field?.name && Array.isArray(field.values) && field.values.length > 0) {
        flat[field.name] = field.values.join(", ");
      }
    });
  }
  return flat;
};

const ICONS = [
  BusinessIcon,
  MessageIcon,
  LabelRoundedIcon,
  InfoRoundedIcon,
  StarRoundedIcon,
];

const pickIcon = (key) => {
  const lowerKey = key.toLowerCase();
  if (lowerKey.includes("company") || lowerKey.includes("business")) return BusinessIcon;
  if (lowerKey.includes("message") || lowerKey.includes("note")) return MessageIcon;
  let sum = 0;
  for (let i = 0; i < key.length; i++) sum += key.charCodeAt(i);
  return ICONS[sum % ICONS.length];
};

export default function CustomFields(p) {
  const theme = useTheme();
  const lead = p.lead || {};

  const [showForm, setShowForm] = useState(false);
  const [keyName, setKeyName] = useState("");
  const [value, setValue] = useState("");

  const [editMode, setEditMode] = useState(false);
  const [draftFields, setDraftFields] = useState({});
  const [deletedKeys, setDeletedKeys] = useState({});

  const prettifyLabel = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  const customFields = lead.custom_fields || {};

  const asDisplayText = (v) => {
    if (v === null || v === undefined) return "";
    if (
      typeof v === "string" ||
      typeof v === "number" ||
      typeof v === "boolean"
    )
      return String(v);
    return JSON.stringify(v);
  };

  useEffect(() => {
    const cleaned = {};
    Object.entries(customFields).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") return;
      if (HIDDEN_KEYS.includes(k)) return;
      if (typeof v === "object") return;
      cleaned[k] = v;
    });
    // Flatten meta_raw_field_data into key-value pairs
    const metaFlat = flattenMetaFields(customFields);
    Object.assign(cleaned, metaFlat);
    setDraftFields(cleaned);
    setDeletedKeys({});
  }, [customFields]);

  const entries = Object.entries(draftFields).filter(
    ([key]) => !RESERVED_CUSTOM_FIELDS.includes(key),
  );

  const updateCustomFields = (updated) => {
    p.updateLead("custom_fields", updated);
  };

  const handleAdd = () => {
    if (!keyName.trim()) return;
    if (!value && value !== 0) return;
    const cleanKey = keyName.trim().toLowerCase().replace(/\s+/g, "_");
    updateCustomFields({ ...draftFields, [cleanKey]: value });
    setKeyName("");
    setValue("");
    setShowForm(false);
  };

  const markDelete = (key) => {
    setDeletedKeys((prev) => ({ ...prev, [key]: true }));
  };

  const handleSaveAll = () => {
    const payload = {};
    Object.entries(draftFields).forEach(([k, v]) => {
      payload[k] = deletedKeys[k] ? null : v;
    });
    updateCustomFields(payload);
    setEditMode(false);
  };

  const handleCancelEdit = () => {
    const cleaned = {};
    Object.entries(customFields).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") return;
      if (HIDDEN_KEYS.includes(k)) return;
      if (typeof v === "object") return;
      cleaned[k] = v;
    });
    const metaFlat = flattenMetaFields(customFields);
    Object.assign(cleaned, metaFlat);
    setDraftFields(cleaned);
    setDeletedKeys({});
    setEditMode(false);
  };

  return (
    <MotionCard
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      sx={{
        borderRadius: 4,
        bgcolor: "#FFFFFF",
        boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
        border: "1px solid #F1F5F9",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Custom Fields
          </Typography>

          <Stack direction="row" spacing={1}>
            {!editMode ? (
              <Button
                size="small"
                startIcon={<EditRoundedIcon sx={{ fontSize: 16 }} />}
                onClick={() => setEditMode(true)}
                sx={{
                  bgcolor: "#FFFFFF",
                  color: "#64748B",
                  px: 2,
                  py: 0.75,
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: 2,
                  border: "1px solid #E2E8F0",
                  textTransform: "none",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    bgcolor: "#F8FAFC",
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                  },
                }}
              >
                Edit
              </Button>
            ) : (
              <>
                <IconButton
                  onClick={handleSaveAll}
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: "rgba(16, 185, 129, 0.12)",
                    color: "#10B981",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: "rgba(16, 185, 129, 0.2)",
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <CheckRoundedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={handleCancelEdit}
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: "rgba(239, 68, 68, 0.12)",
                    color: "#EF4444",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: "rgba(239, 68, 68, 0.2)",
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </>
            )}
            <Button
              size="small"
              startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />}
              onClick={() => setShowForm((s) => !s)}
              sx={{
                px: 2,
                py: 0.75,
                fontWeight: 600,
                fontSize: 13,
                borderRadius: 2,
                textTransform: "none",
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                color: "#FFFFFF",
                boxShadow: "0 4px 14px rgba(124, 58, 237, 0.25)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 20px rgba(124, 58, 237, 0.35)",
                },
              }}
            >
              Add
            </Button>
          </Stack>
        </Stack>

        <AnimatePresence>
          {showForm && (
            <MotionBox
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              sx={{ mb: 2.5, overflow: "hidden" }}
            >
              <Stack spacing={1.5} direction={{ xs: "column", sm: "row" }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Field Name"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Field Value"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleAdd}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    fontWeight: 600,
                    textTransform: "none",
                  }}
                >
                  Save
                </Button>
              </Stack>
            </MotionBox>
          )}
        </AnimatePresence>

        {entries.length === 0 ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              py: 5,
              borderRadius: 3,
              bgcolor: "#FAFAFA",
              border: "1px dashed #E2E8F0",
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: "#64748B",
                textAlign: "center",
              }}
            >
              No custom fields added yet
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                mt: 0.5,
                color: "#94A3B8",
                textAlign: "center",
              }}
            >
              Click the Add button to create one
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {entries.map(([key, val], index) => {
              const Icon = pickIcon(key);
              const isDeleted = deletedKeys[key];
              return (
                <MotionBox
                  key={key}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  whileHover={{ scale: 1.01 }}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: "#FAFAFA",
                    border: "1px solid #F1F5F9",
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                    opacity: isDeleted ? 0.5 : 1,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      bgcolor: "#F8FAFC",
                      borderColor: "#E2E8F0",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: `${theme.palette.primary.main}1F`,
                      color: theme.palette.primary.main,
                      width: 44,
                      height: 44,
                      borderRadius: 2.5,
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: 22 }} />
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {editMode ? (
                      <TextField
                        fullWidth
                        size="small"
                        label={prettifyLabel(key)}
                        value={asDisplayText(val)}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) =>
                          setDraftFields({
                            ...draftFields,
                            [key]: e.target.value,
                          })
                        }
                        sx={{
                          textDecoration: isDeleted ? "line-through" : "none",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    ) : (
                      <>
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 600,
                            color: "#94A3B8",
                            textTransform: "uppercase",
                            letterSpacing: 0.8,
                            mb: 0.25,
                            textDecoration: isDeleted ? "line-through" : "none",
                            wordBreak: "break-word",
                          }}
                        >
                          {prettifyLabel(key)}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#1E293B",
                            textDecoration: isDeleted ? "line-through" : "none",
                            wordBreak: "break-word",
                          }}
                        >
                          {asDisplayText(val)}
                        </Typography>
                      </>
                    )}
                  </Box>

                  {editMode && !isDeleted && (
                    <IconButton
                      onClick={() => markDelete(key)}
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: "rgba(239, 68, 68, 0.12)",
                        color: "#EF4444",
                        flexShrink: 0,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: "rgba(239, 68, 68, 0.2)",
                          transform: "scale(1.05)",
                        },
                      }}
                    >
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  )}
                </MotionBox>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </MotionCard>
  );
}
